import React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import ScrollToTop from "./ScrollToTop";
import SEO from "../components/seo/SEO";

import App from "../App";

import Home from "../pages/home/Home";
import BookDetail from "../pages/books/book-details/BookDetails";
import Login from "../pages/auth/login/Login";
import Register from "../pages/auth/register/Register";
import ForgotPassword from "../pages/auth/forgot-password/ForgotPassword";
import VerifyOTP from "../pages/auth/forgot-password/VerifyOTP";
import NotFound from "../pages/not-found/NotFound";
import Profile from "../pages/auth/profile/Profile";
import BookList from "../pages/books/book-list/BookList";
import UnifiedCheckout from "../pages/checkout/UnifiedCheckout";
import About from "../pages/about/About";
import UnifiedSuccess from "../pages/checkout/UnifiedSuccess";
import UnifiedError from "../pages/checkout/UnifiedError";
import AudioBookList from "../pages/books/audio-book-list/AudioBookList";
import Subscription from "../pages/subscription/Subscription";
import Magazine from "../pages/magazine/Magazine";

// help pages
import FAQs from "../pages/help/faqs/FAQs";
import Terms from "../pages/help/terms/Terms";
import PrivacyPolicy from "../pages/help/privacy-policy/PrivacyPolicy";
import ReturnsPolicy from "../pages/help/returns-policy/ReturnsPolicy";
import ContactUs from "../pages/help/contact-us/ContactUs";
import Team from "../pages/team/Team";
import FreeBooks from "../pages/free-books/FreeBooks";

// SEO Wrapper Component
const SEOWrapper = ({ children, title, description, type = "website" }) => (
  <>
    <SEO title={title} description={description} type={type} />
    {children}
  </>
);

const IndexRouter = () => {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="" element={<App />}>
          <Route
            path="/"
            element={
              <SEOWrapper
                title="Home"
                description="Welcome to Hasa Book Store - Your trusted source for educational books and resources."
              >
                <Home />
              </SEOWrapper>
            }
          />

          <Route
            path="/books"
            element={
              <SEOWrapper
                title="Books"
                description="Explore our collection of educational books, textbooks, and learning resources."
                type="book.list"
              >
                <BookList />
              </SEOWrapper>
            }
          />

          <Route
            path="/audio-books"
            element={
              <SEOWrapper
                title="Audio Books"
                description="Explore our collection of educational audio books."
              >
                <AudioBookList />
              </SEOWrapper>
            }
          />

          <Route path="/books/:id" element={<BookDetail />} />

          {/* Protected Routes */}
          <Route
            path="/checkout"
            element={
              <ProtectedRoute>
                <SEOWrapper
                  title="Checkout"
                  description="Secure checkout process for your purchase."
                >
                  <UnifiedCheckout />
                </SEOWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <SEOWrapper
                  title="My Profile"
                  description="Manage your Hasa Book Store account and preferences."
                >
                  <Profile />
                </SEOWrapper>
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment-success"
            element={
                <SEOWrapper
                  title="Purchase Successful"
                  description="Your payment has been processed successfully."
                >
                  <UnifiedSuccess />
                </SEOWrapper>
            }
          />

          <Route
            path="/payment-error"
            element={
              <SEOWrapper
                title="Payment Error"
                description="An error occurred while processing your payment."
              >
                <UnifiedError />
              </SEOWrapper>
            }
          />

          <Route
            path="/subscription"
            element={
              <SEOWrapper
                title="Subscription Plans"
                description="Choose a subscription plan to get unlimited access to our library of books and audiobooks."
                type="product.group"
              >
                <Subscription />
              </SEOWrapper>
            }
          />

          {/* Public Routes */}
          <Route
            path="/about"
            element={
              <SEOWrapper
                title="About Us"
                description="Learn about Hasa Book Store's mission to provide quality educational resources."
              >
                <About />
              </SEOWrapper>
            }
          />

          <Route
            path="/team"
            element={
              <SEOWrapper
                title="Our Team"
                description="Meet the dedicated team behind Hasa Book Store."
              >
                <Team />
              </SEOWrapper>
            }
          />

          <Route
            path="/free-books"
            element={
              <SEOWrapper
                title="Free Books"
                description="Access free educational resources and materials from Hasa Book Store."
              >
                <FreeBooks />
              </SEOWrapper>
            }
          />

          {/* Help Routes */}
          <Route
            path="/help/faqs"
            element={
              <SEOWrapper
                title="FAQs"
                description="Find answers to frequently asked questions about Hasa Book Store services."
              >
                <FAQs />
              </SEOWrapper>
            }
          />

          <Route
            path="/help/terms"
            element={
              <SEOWrapper
                title="Terms of Service"
                description="Read our terms of service and usage policies."
              >
                <Terms />
              </SEOWrapper>
            }
          />

          <Route
            path="/help/privacy-policy"
            element={
              <SEOWrapper
                title="Privacy Policy"
                description="Learn how we protect your privacy and handle your data."
              >
                <PrivacyPolicy />
              </SEOWrapper>
            }
          />

          <Route
            path="/help/returns-policy"
            element={
              <SEOWrapper
                title="Returns Policy"
                description="Understand our returns and refund policies."
              >
                <ReturnsPolicy />
              </SEOWrapper>
            }
          />

          <Route
            path="/help/contact-us"
            element={
              <SEOWrapper
                title="Contact Us"
                description="Get in touch with Hasa Book Store for support and inquiries."
              >
                <ContactUs />
              </SEOWrapper>
            }
          />

          {/* Add Magazine Routes */}
          <Route
            path="/magazines"
            element={
              <SEOWrapper
                title="Digital Magazines"
                description="Explore our curated collection of digital magazines. Access premium content across various categories."
                type="magazine.list"
              >
                <Magazine />
              </SEOWrapper>
            }
          />

          <Route
            path="/magazines"
            element={
              <ProtectedRoute>
                <SEOWrapper
                  title="Magazine Details"
                  description="Read your favorite digital magazine with our interactive reader."
                  type="magazine"
                >
                  <Magazine />
                </SEOWrapper>
              </ProtectedRoute>
            }
          />

          {/* Auth Routes */}
          <Route
            path="/login"
            element={
              <SEOWrapper
                title="Login"
                description="Sign in to your Hasa Book Store account."
              >
                <Login />
              </SEOWrapper>
            }
          />

          <Route
            path="/register"
            element={
              <SEOWrapper
                title="Register"
                description="Create a new account with Hasa Book Store."
              >
                <Register />
              </SEOWrapper>
            }
          />

          {/* Password Reset Routes */}
          <Route
            path="/forgot-password"
            element={
              <SEOWrapper
                title="Forgot Password"
                description="Reset your Hasa Book Store account password."
              >
                <ForgotPassword />
              </SEOWrapper>
            }
          />

          <Route
            path="/verify-otp"
            element={
              <SEOWrapper
                title="Verify OTP"
                description="Verify your identity and set a new password."
              >
                <VerifyOTP />
              </SEOWrapper>
            }
          />

          <Route
            path="*"
            element={
              <SEOWrapper
                title="Page Not Found"
                description="The page you're looking for doesn't exist."
              >
                <NotFound />
              </SEOWrapper>
            }
          />
        </Route>
      </Routes>
    </Router>
  );
};

export default IndexRouter;
